import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import cx from 'classnames'
import prettyBytes from 'pretty-bytes'

import Alert from 'common/components/alert/Alert'
import Button from 'common/components/button'
import Loader from 'common/components/loaders/Loader'
import { CloseSmall, File as FileIcon } from 'common/icons'

const MAX_PDF_SIZE_BYTES = 20971520 // 20MB

export type PdfSelection = {
  file: File
  thumbnailUrl: string | null
  extractedText: string | null
  isProcessing: boolean
  error: string | null
}

type Props = {
  selection: PdfSelection | null
  onFileSelected: (file: File) => void
  onRemoveFile: () => void
}

const PdfImportTabContent = ({
  selection,
  onFileSelected,
  onRemoveFile,
}: Props) => {
  const [rejectionError, setRejectionError] = useState<string | null>(null)
  const isBusy = !!selection?.isProcessing

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    maxSize: MAX_PDF_SIZE_BYTES,
    noClick: isBusy,
    noDrag: isBusy,
    noKeyboard: isBusy,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setRejectionError(
          fileRejections[0].errors[0]?.message ??
            'This file could not be accepted.'
        )
        return
      }
      setRejectionError(null)
      const file = acceptedFiles[0]
      if (file) {
        onFileSelected(file)
      }
    },
  })

  const displayError = rejectionError ?? selection?.error ?? null

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {displayError && (
        <Alert type="error" showIcon className="mb-4 shrink-0">
          {displayError}
        </Alert>
      )}
      <div
        {...getRootProps()}
        data-testid="pdf-upload-dropzone"
        className={cx(
          'flex-1 min-h-0 flex flex-col items-center justify-center gap-4 rounded-sm px-6 py-8 outline-none transition-colors bg-gold-300/25',
          {
            'cursor-pointer': !isBusy,
            'cursor-not-allowed': isBusy,
          }
        )}
      >
        <input {...getInputProps()} />
        {selection ? (
          <>
            {selection.isProcessing ? (
              <Loader size="small" />
            ) : selection.thumbnailUrl ? (
              <div className="flex-1 min-h-0 w-full flex items-center justify-center">
                <img
                  src={selection.thumbnailUrl}
                  alt="PDF preview"
                  className="h-full w-full object-contain rounded-md border-2 border-gold-400 shadow-md bg-white p-2"
                  data-testid="pdf-thumbnail"
                />
              </div>
            ) : (
              <FileIcon
                size={12}
                isDefaultColor={false}
                className="text-coolGray-400"
              />
            )}
            <div className="flex items-center gap-2 text-base-700 shrink-0 bg-white rounded-full border border-gold-400 shadow-sm px-4 py-2">
              <span className="text-sm font-medium" data-testid="pdf-file-name">
                {selection.file.name} ({prettyBytes(selection.file.size)})
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemoveFile()
                }}
                data-testid="pdf-file-remove-button"
              >
                <CloseSmall size={3} isDefaultColor={false} />
              </button>
            </div>
          </>
        ) : (
          <>
            <FileIcon
              size={12}
              isDefaultColor={false}
              className="text-coolGray-400"
            />
            <div className="text-center">
              <p className="text-2xl font-relais text-base-700">
                {isDragActive
                  ? 'Drop your file here'
                  : 'Drag & drop your file here'}
              </p>
              <p className="text-sm text-base-500 pt-2">
                Supports PDF files up to 20MB
              </p>
            </div>
            <Button
              variant="secondary"
              size="medium"
              data-testid="pdf-browse-button"
            >
              Browse files
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default PdfImportTabContent
